import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useBranding } from '../hooks/useBranding';

interface PropertyGroupSettings {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  monthlyDuesAmount: number;
  lateFeeAmount: number;
  paymentDueDays: number;
  contactEmail: string;
  contactPhone: string;
  allowOnlinePayments: boolean;
  requireApprovalForEvents: boolean;
  maxGuestsPerResident: number;
}

const MAX_LOGO_RAW_BYTES = 1_500_000;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { branding, refresh: refreshBranding } = useBranding();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMessage, setLogoMessage] = useState<string | null>(null);
  const [settings, setSettings] = useState<PropertyGroupSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const handleLogoFile = async (file: File) => {
    setLogoMessage(null);
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setLogoMessage('Please choose a PNG, JPG, SVG, or WEBP image.');
      return;
    }
    if (file.size > MAX_LOGO_RAW_BYTES) {
      setLogoMessage(`Logo too large (${Math.round(file.size / 1024)}KB). Max 1.5MB.`);
      return;
    }
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) {
      setLogoMessage('No property group selected.');
      return;
    }
    setLogoBusy(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await api.put(`/api/property-groups/${selectedGroupId}/logo`, { logoDataUrl: dataUrl });
      await refreshBranding();
      setLogoMessage('Logo updated.');
      setTimeout(() => setLogoMessage(null), 2500);
    } catch (err: any) {
      setLogoMessage(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setLogoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) return;
    setLogoBusy(true);
    setLogoMessage(null);
    try {
      await api.put(`/api/property-groups/${selectedGroupId}/logo`, { logoDataUrl: null });
      await refreshBranding();
      setLogoMessage('Logo removed.');
      setTimeout(() => setLogoMessage(null), 2500);
    } catch (err: any) {
      setLogoMessage(err.response?.data?.message || 'Failed to remove logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      if (!selectedGroupId) {
        console.log('No property group selected');
        return;
      }

      const response = await api.get(`/api/property-groups/${selectedGroupId}/settings`);
      setSettings(response.data as PropertyGroupSettings);
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      setMessage(error.response?.data?.message || 'Error loading settings');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user?.role === 'ADMIN' && user?.token) {
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      if (selectedGroupId) {
        fetchSettings();
      } else {
        setLoading(false);
      }
    }
  }, [user, fetchSettings]);

  const handleInputChange = (field: keyof PropertyGroupSettings, value: string | number | boolean) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handleSave = async () => {
    try {
      if (!settings) return;
      
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      if (!selectedGroupId) return;

      await api.put(`/api/property-groups/${selectedGroupId}/settings`, settings);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setMessage(error.response?.data?.message || 'Error saving settings');
    }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Access denied. Only administrators can access settings.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-white">Loading...</div>
      </div>
    );
  }

  if (!localStorage.getItem('selectedPropertyGroup')) {
    return (
      <div className="p-6">
        <div className="floating-card text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Settings</h1>
          <p className="text-white/60">Please select a property group to view its settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {message && (
        <div className={`p-3 rounded-lg ${
          message.includes('successfully') 
            ? 'bg-green-500/20 border border-green-500/30 text-green-400'
            : 'bg-red-500/20 border border-red-500/30 text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* Branding card — per-property logo shown in nav for admin, residents, and vendors */}
      <div className="floating-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Branding</h2>
            <p className="text-sm text-white/60 mt-1">
              Upload a logo to replace the BlockBoard wordmark for your admins, residents, and vendors inside this property.
            </p>
          </div>
        </div>
        {logoMessage && (
          <div className={`p-2 mb-4 rounded-lg text-sm ${
            logoMessage.toLowerCase().includes('fail') || logoMessage.toLowerCase().includes('too large') || logoMessage.toLowerCase().includes('choose')
              ? 'bg-red-500/20 border border-red-500/30 text-red-400'
              : 'bg-green-500/20 border border-green-500/30 text-green-400'
          }`}>
            {logoMessage}
          </div>
        )}
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
            {branding?.logoUrl ? (
              <img src={branding.logoUrl} alt="Current logo" className="w-full h-full object-contain p-2" />
            ) : (
              <span className="text-xs text-white/40 text-center px-2">No logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleLogoFile(f);
              }}
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={logoBusy}
                onClick={() => fileInputRef.current?.click()}
                className="btn-primary disabled:opacity-50"
              >
                {logoBusy ? 'Uploading...' : branding?.logoUrl ? 'Replace logo' : 'Upload logo'}
              </button>
              {branding?.logoUrl && (
                <button
                  type="button"
                  disabled={logoBusy}
                  onClick={handleRemoveLogo}
                  className="btn-secondary disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-white/40">PNG, JPG, SVG, or WEBP. Max 1.5MB. Square images work best.</p>
          </div>
        </div>
      </div>

      {settings ? (
        <div className="floating-card">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-white">
              Property Group Settings
              <span className="text-lg font-normal text-white/60 ml-2">
                ({settings.name})
              </span>
            </h1>
            <button
              onClick={handleSave}
              className="btn-primary"
            >
              Save Changes
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white border-b border-white/20 pb-2">Basic Information</h2>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Property Group Name</label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Address</label>
                <input
                  type="text"
                  value={settings.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">City</label>
                  <input
                    type="text"
                    value={settings.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">State</label>
                  <input
                    type="text"
                    value={settings.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">ZIP Code</label>
                <input
                  type="text"
                  value={settings.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className="input-field"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white border-b border-white/20 pb-2">Financial Settings</h2>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Monthly Dues Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.monthlyDuesAmount}
                  onChange={(e) => handleInputChange('monthlyDuesAmount', parseFloat(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Late Fee Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.lateFeeAmount}
                  onChange={(e) => handleInputChange('lateFeeAmount', parseFloat(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Payment Due Days</label>
                <input
                  type="number"
                  value={settings.paymentDueDays}
                  onChange={(e) => handleInputChange('paymentDueDays', parseInt(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={settings.contactEmail}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={settings.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <h2 className="text-lg font-semibold text-white mb-4">Policy Settings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-white/80">Allow Online Payments</span>
                <input
                  type="checkbox"
                  checked={settings.allowOnlinePayments}
                  onChange={(e) => handleInputChange('allowOnlinePayments', e.target.checked)}
                  className="checkbox"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-white/80">Require Approval for Events</span>
                <input
                  type="checkbox"
                  checked={settings.requireApprovalForEvents}
                  onChange={(e) => handleInputChange('requireApprovalForEvents', e.target.checked)}
                  className="checkbox"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-white/80">Max Guests Per Resident</span>
                <input
                  type="number"
                  value={settings.maxGuestsPerResident}
                  onChange={(e) => handleInputChange('maxGuestsPerResident', parseInt(e.target.value))}
                  className="input-field w-20"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="floating-card text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Settings</h1>
          <p className="text-white/60">Error loading property group settings</p>
        </div>
      )}
    </div>
  );
};

export default Settings;